import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Search from '@mui/icons-material/Search';
import Clear from '@mui/icons-material/Clear';
import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined';

interface GlobalSearchBarProps {
  value: string;
  onChange: (value: string) => void;
  onSearch: () => void;
  /** Called when the user clicks the X clear button — parent should reset search + filters */
  onClearAll?: () => void;
  /** When provided, a smart-search toggle appears; pressing Enter with a non-empty query calls this instead of onSearch */
  onSmartSearch?: (query: string) => void;
  placeholder?: string;
}

export const GlobalSearchBar: React.FC<GlobalSearchBarProps> = ({
  value,
  onChange,
  onSearch,
  onClearAll,
  onSmartSearch,
  placeholder = "Search across all call fields..."
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [smartSearchEnabled, setSmartSearchEnabled] = useState(true);

  // Keep local input in sync when parent resets the value (e.g. Clear All filters)
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setLocalValue(newValue);
    onChange(newValue);
  }, [onChange]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      if (onSmartSearch && smartSearchEnabled && localValue.trim()) {
        onSmartSearch(localValue.trim());
      } else {
        onSearch();
      }
    }
  }, [onSearch, onSmartSearch, smartSearchEnabled, localValue]);

  const handleSearchClick = useCallback(() => {
    if (onSmartSearch && smartSearchEnabled && localValue.trim()) {
      onSmartSearch(localValue.trim());
    } else {
      onSearch();
    }
  }, [onSearch, onSmartSearch, smartSearchEnabled, localValue]);

  const handleClear = useCallback(() => {
    setLocalValue('');
    onChange('');
    // If parent provided a full reset callback, use it (clears filters + semantic mode too)
    if (onClearAll) {
      onClearAll();
    } else {
      onSearch();
    }
  }, [onChange, onSearch, onClearAll]);

  const smartToggleTitle = onSmartSearch
    ? smartSearchEnabled
      ? 'Smart search ON — uses AI to find semantically similar calls. Click to switch to keyword search.'
      : 'Smart search OFF — using keyword search. Click to enable AI-powered search.'
    : undefined;

  return (
    <Box sx={{ width: '100%', maxWidth: 600 }}>
      <TextField
        fullWidth
        variant="outlined"
        placeholder={placeholder}
        value={localValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Tooltip title="Search">
                <IconButton onClick={handleSearchClick} edge="start" aria-label="Search">
                  <Search />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {onSmartSearch && (
                <Tooltip title={smartToggleTitle}>
                  <IconButton
                    onClick={() => setSmartSearchEnabled((prev) => !prev)}
                    edge="end"
                    size="small"
                    aria-label={smartSearchEnabled ? 'Disable smart search' : 'Enable smart search'}
                    sx={{ color: smartSearchEnabled ? 'primary.main' : 'action.disabled', mr: 0.5 }}
                  >
                    <AutoAwesomeOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {localValue && (
                <Tooltip title="Clear search">
                  <IconButton onClick={handleClear} edge="end" aria-label="Clear search">
                    <Clear />
                  </IconButton>
                </Tooltip>
              )}
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'background.paper',
            '&:hover': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
            },
          },
        }}
      />
    </Box>
  );
};